"use client";

import Link from "@/infrastructure/router/Link";
import {
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	EmptyState,
	GlassIconButton,
	MessageBubble,
	MessageInputBar,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	type Conversation,
	type Message,
	offlineSocialRepository,
	type SocialRepository,
	sameScope,
	scopeKey,
} from "@/domain/social/SocialRepository";

const SELF_PARTICIPANT_MARKER = "你";
const UNKNOWN_PEER_LABEL = "未知会话";
const SEND_PLACEHOLDER = "输入消息";
const EMPTY_STATE_TITLE = "开始你的对话";
const EMPTY_STATE_MESSAGE = "发送第一条消息，与对方开启合作与灵感交流。";
const SCROLL_TO_BOTTOM_LABEL = "滚动到最新消息";
const MESSAGE_LOAD_ERROR = "消息记录加载失败，请稍后重试。";
const SEND_FAILED_NOTICE = "消息未发送，点按重试。";

const LONG_PRESS_MS = 450;

function useFence() {
	const scope = useArtistSession().scope;
	const current = useRef(scope);
	const generations = useRef(new Map<string, number>());
	useEffect(() => {
		current.current = scope;
		generations.current.clear();
		return () => {
			current.current = null;
			generations.current.clear();
		};
	}, [scope]);
	return useCallback((requestKey: string) => {
		const expectedScope = current.current;
		const expectedGeneration = (generations.current.get(requestKey) ?? 0) + 1;
		generations.current.set(requestKey, expectedGeneration);
		return () =>
			sameScope(current.current, expectedScope) &&
			generations.current.get(requestKey) === expectedGeneration;
	}, []);
}

type MessageThreadDialog =
	| { readonly kind: "conversationActions" }
	| { readonly kind: "messageActions"; readonly message: Message };

export function MessageThread({
	id,
	repository = offlineSocialRepository,
}: Readonly<{ id: string; repository?: SocialRepository }>) {
	const auth = useArtistSession();
	const fence = useFence();

	const [conversation, setConversation] = useState<Conversation | null>(null);
	const [messages, setMessages] = useState<readonly Message[] | null>(null);
	const [draft, setDraft] = useState("");
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [dialog, setDialog] = useState<MessageThreadDialog | null>(null);
	const [isMuted, setIsMuted] = useState(false);
	const [isBlocked, setIsBlocked] = useState(false);
	const [removedMessageIds, setRemovedMessageIds] = useState<
		ReadonlySet<string>
	>(() => new Set());

	const logRef = useRef<HTMLDivElement>(null);
	const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

	const draftStorageKey = useMemo(
		() => `message:${scopeKeySafe(auth.scope)}:${id}`,
		[auth.scope, id],
	);

	useEffect(() => {
		const activeScope = auth.scope;
		if (activeScope === null) return;
		const applies = fence(`thread.load:${id}`);
		setConversation(null);
		setMessages(null);
		setError(null);
		setNotice(null);
		setRemovedMessageIds(new Set());
		setDraft(readDraft(draftStorageKey));
		Promise.all([
			repository.loadInbox(activeScope),
			repository.loadMessages(activeScope, id),
		])
			.then(([inbox, loaded]) => {
				if (!applies()) return;
				const match = inbox.find((row) => row.id === id) ?? null;
				setConversation(match);
				setIsMuted(match?.muted === true);
				setIsBlocked(match?.blocked === true);
				setMessages([...loaded].sort((a, b) => a.at - b.at));
				void repository.markRead(activeScope, id).catch(() => undefined);
			})
			.catch(() => {
				if (applies()) setError(MESSAGE_LOAD_ERROR);
			});
		return () => {
			applies();
		};
	}, [auth.scope, fence, id, draftStorageKey, repository]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional extra dep — re-run on messages change to auto-scroll
	useEffect(() => {
		const log = logRef.current;
		if (log === null) return;
		if (isPinnedToBottom) {
			log.scrollTop = log.scrollHeight;
		}
	}, [messages, isPinnedToBottom]);

	const peerName = useMemo(() => {
		if (conversation !== null) {
			return formatParticipants(conversation.participants);
		}
		return UNKNOWN_PEER_LABEL;
	}, [conversation]);

	const visibleMessages = useMemo(() => {
		if (messages === null) return null;
		return messages.filter(
			(message) => !removedMessageIds.has(message.clientId),
		);
	}, [messages, removedMessageIds]);

	const handleLogScroll = useCallback(() => {
		const log = logRef.current;
		if (log === null) return;
		const distanceFromBottom =
			log.scrollHeight - log.scrollTop - log.clientHeight;
		setIsPinnedToBottom(distanceFromBottom < 64);
	}, []);

	const scrollToBottom = useCallback(() => {
		const log = logRef.current;
		if (log === null) return;
		log.scrollTo({ top: log.scrollHeight, behavior: "smooth" });
		setIsPinnedToBottom(true);
	}, []);

	const send = useCallback(
		async (retryClientId?: string, retryBody?: string): Promise<void> => {
			const body = (retryBody ?? draft).trim();
			if (body.length === 0) return;
			const activeScope = auth.scope;
			if (activeScope === null) return;

			const clientId = retryClientId ?? crypto.randomUUID();
			const optimistic: Message = {
				id: `local-${clientId}`,
				clientId,
				body,
				author: "me",
				at: Date.now(),
				state: "sending",
			};

			setMessages((current) => {
				const exists =
					current?.some((message) => message.clientId === clientId) ?? false;
				if (exists) return current;
				return [...(current ?? []), optimistic].sort((a, b) => a.at - b.at);
			});
			setIsPinnedToBottom(true);

			if (retryClientId === undefined) {
				setDraft("");
				clearDraft(draftStorageKey);
			}

			const applies = fence(`message.send:${clientId}`);
			try {
				const result = await repository.sendMessage(
					activeScope,
					id,
					optimistic,
				);
				if (!applies()) return;
				setMessages((current) =>
					(current ?? []).map((message) =>
						message.clientId === clientId
							? {
									...message,
									state: result.kind === "sent" ? "sent" : "failed",
								}
							: message,
					),
				);
				if (result.kind === "failed") setNotice(SEND_FAILED_NOTICE);
			} catch {
				if (applies()) setNotice(SEND_FAILED_NOTICE);
			}
		},
		[auth.scope, draft, draftStorageKey, fence, id, repository],
	);

	const handleDraftChange = useCallback(
		(value: string) => {
			setDraft(value);
			writeDraft(draftStorageKey, value);
		},
		[draftStorageKey],
	);

	const handleCopyMessage = useCallback(async (message: Message) => {
		setDialog(null);
		if (
			typeof navigator !== "undefined" &&
			navigator.clipboard !== undefined &&
			typeof navigator.clipboard.writeText === "function"
		) {
			try {
				await navigator.clipboard.writeText(message.body);
				setNotice("已复制到剪贴板。");
			} catch {
				setNotice("复制失败，请手动选择文本。");
			}
		} else {
			setNotice("复制失败，请手动选择文本。");
		}
	}, []);

	const handleDeleteMessage = useCallback(async (message: Message) => {
		setDialog(null);
		const activeScope = auth.scope;
		if (activeScope === null) return;
		setRemovedMessageIds((current) => {
			const next = new Set(current);
			next.add(message.clientId);
			return next;
		});
		try {
			await repository.removeMessage(activeScope, id, message.clientId);
			setNotice("消息已移除。");
		} catch {
			setNotice("删除消息失败，请稍后重试。");
		}
	}, [auth.scope, id, repository]);

	const handleForwardMessage = useCallback((message: Message) => {
		setDialog(null);
		const forwarded = `转发：${message.body}`;
		setDraft(forwarded);
		writeDraft(draftStorageKey, forwarded);
		setNotice("消息已放入编辑框，可补充内容后发送。");
	}, [draftStorageKey]);

	const handleConversationAction = useCallback(
		async (action: "mute" | "clear" | "block") => {
			setDialog(null);
			const activeScope = auth.scope;
			if (activeScope === null) return;
			try {
				if (action === "mute") {
					const next = !isMuted;
					await repository.setConversationMuted(activeScope, id, next);
					setIsMuted(next);
					setNotice(next ? "已静音此会话。" : "已恢复会话通知。");
				} else if (action === "clear") {
					await repository.clearMessages(activeScope, id);
					setMessages([]);
					setRemovedMessageIds(new Set());
					setNotice("对话已清空。");
				} else {
					const next = !isBlocked;
					await repository.setConversationBlocked(activeScope, id, next);
					setIsBlocked(next);
					setNotice(next ? "已屏蔽该会话。" : "已解除屏蔽。");
				}
			} catch {
				setNotice("会话操作失败，请稍后重试。");
			}
		},
		[auth.scope, id, isBlocked, isMuted, repository],
	);

	if (auth.phase === "loading") {
		return (
			<section
				className="native-page message-thread-page"
				aria-busy="true"
				aria-label="正在载入会话"
			>
				<NavigationBar title={UNKNOWN_PEER_LABEL} backHref="/messages/inbox" />
				<MessageThreadSkeleton />
			</section>
		);
	}

	if (auth.phase === "error") {
		return (
			<section className="native-page message-thread-page" aria-label="会话">
				<NavigationBar title={UNKNOWN_PEER_LABEL} backHref="/messages/inbox" />
				<Toast style="error">会话暂时无法载入。</Toast>
			</section>
		);
	}

	if (auth.phase === "guest" || auth.session === null) {
		return (
			<section className="native-page message-thread-page" aria-label="会话">
				<NavigationBar title={UNKNOWN_PEER_LABEL} backHref="/messages/inbox" />
				<EmptyState
					title="登录后查看对话"
					message="登录后即可与好友、工作室私信交流。"
					symbol={<ArtistIcon name="message" size={42} />}
				/>
				<Link className="primary-action" href="/auth">
					登录
				</Link>
			</section>
		);
	}

	if (messages === null) {
		return (
			<section
				className="native-page message-thread-page"
				aria-busy="true"
				aria-label="正在载入会话"
			>
				<NavigationBar title={peerName} backHref="/messages/inbox" />
				<MessageThreadSkeleton />
			</section>
		);
	}

	const trailingActions = (
		<GlassIconButton
			label="更多操作"
			onClick={() => setDialog({ kind: "conversationActions" })}
		>
			<ArtistIcon name="more" size={22} />
		</GlassIconButton>
	);

	const hasVisibleMessages =
		visibleMessages !== null && visibleMessages.length > 0;

	return (
		<section
			className="native-page message-thread-page"
			aria-label={`与 ${peerName} 的对话`}
		>
			<NavigationBar
				title={peerName}
				backHref="/messages/inbox"
				trailing={trailingActions}
			/>
			{notice !== null ? (
				<Toast
					style={
						notice.includes("失败") || notice.includes("未发送")
							? "error"
							: "info"
					}
					onClose={() => setNotice(null)}
				>
					{notice}
				</Toast>
			) : null}
			{error !== null ? <Toast style="error">{error}</Toast> : null}

			<div className="message-thread-body">
				{hasVisibleMessages ? (
					<div
						ref={logRef}
						className="message-thread-log"
						role="log"
						aria-live="polite"
						aria-label="消息记录"
						onScroll={handleLogScroll}
					>
						{(visibleMessages ?? []).map((message, index) => {
							const previous =
								index > 0 ? (visibleMessages ?? [])[index - 1] : undefined;
							const showAuthorLabel =
								message.author === "other" &&
								(previous === undefined || previous.author !== "other");
							return (
								<MessageThreadRow
									key={message.clientId}
									message={message}
									peerName={peerName}
									showAuthorLabel={showAuthorLabel}
									onOpenActions={(target) =>
										setDialog({ kind: "messageActions", message: target })
									}
									onRetry={(target) => void send(target.clientId, target.body)}
								/>
							);
						})}
					</div>
				) : (
					<div className="message-thread-empty">
						<EmptyState
							title={EMPTY_STATE_TITLE}
							message={EMPTY_STATE_MESSAGE}
							symbol={<ArtistIcon name="message" size={42} />}
						/>
					</div>
				)}

				{hasVisibleMessages && !isPinnedToBottom ? (
					<button
						type="button"
						className="message-thread-scroll-button"
						aria-label={SCROLL_TO_BOTTOM_LABEL}
						onClick={scrollToBottom}
					>
						<ArtistIcon name="chevron" size={18} strokeWidth={2.4} />
					</button>
				) : null}
			</div>

			<MessageInputBar
				label={isBlocked ? "已屏蔽该会话" : SEND_PLACEHOLDER}
				value={draft}
				onChange={handleDraftChange}
				onSend={() => void send()}
				disabled={isBlocked || draft.trim().length === 0}
			/>

			<MessageThreadDialogSurface
				dialog={dialog}
				peerName={peerName}
				isMuted={isMuted}
				isBlocked={isBlocked}
				onClose={() => setDialog(null)}
				onCopyMessage={handleCopyMessage}
				onDeleteMessage={handleDeleteMessage}
				onForwardMessage={handleForwardMessage}
				onConversationAction={handleConversationAction}
			/>
		</section>
	);
}


interface MessageThreadRowProps {
	readonly message: Message;
	readonly peerName: string;
	readonly showAuthorLabel: boolean;
	readonly onOpenActions: (message: Message) => void;
	readonly onRetry: (message: Message) => void;
}

function MessageThreadRow({
	message,
	peerName,
	showAuthorLabel,
	onOpenActions,
	onRetry,
}: Readonly<MessageThreadRowProps>) {
	const mine = message.author === "me";
	const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressTriggeredRef = useRef(false);

	const clearPressTimer = useCallback(() => {
		if (pressTimerRef.current !== null) {
			clearTimeout(pressTimerRef.current);
			pressTimerRef.current = null;
		}
	}, []);

	const startPress = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (event.pointerType === "mouse") return;
			longPressTriggeredRef.current = false;
			clearPressTimer();
			pressTimerRef.current = setTimeout(() => {
				longPressTriggeredRef.current = true;
				onOpenActions(message);
			}, LONG_PRESS_MS);
		},
		[clearPressTimer, message, onOpenActions],
	);

	const cancelPress = useCallback(() => {
		clearPressTimer();
	}, [clearPressTimer]);

	const handleContextMenu = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			event.preventDefault();
			onOpenActions(message);
		},
		[message, onOpenActions],
	);

	const handleClick = useCallback(() => {
		if (longPressTriggeredRef.current) {
			longPressTriggeredRef.current = false;
			return;
		}
	}, []);

	useEffect(() => clearPressTimer, [clearPressTimer]);

	const timestamp = formatTimestamp(message.at);
	const meta = mine && message.state === "failed" ? "发送失败" : timestamp;

	return (
		<div
			className={
				mine
					? "message-thread-row message-thread-row--mine"
					: "message-thread-row"
			}
		>
			{showAuthorLabel && !mine ? (
				<small className="message-thread-row__author">{peerName}</small>
			) : null}
			{/* biome-ignore lint/a11y/useSemanticElements: bubble wraps MessageBubble's <article> (flow content) which is not valid phrasing content inside a <button>; div+role=button is the accessible custom-control pattern for long-press + contextmenu actions */}
			<div
				className="message-thread-row__bubble"
				role="button"
				tabIndex={0}
				aria-label={`消息：${message.body}，${timestamp}，长按或右键打开操作`}
				onPointerDown={startPress}
				onPointerUp={cancelPress}
				onPointerLeave={cancelPress}
				onPointerCancel={cancelPress}
				onContextMenu={handleContextMenu}
				onClick={handleClick}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						onOpenActions(message);
					}
				}}
			>
				<MessageBubble
					mine={mine}
					meta={meta}
					failedAction={
						mine && message.state === "failed" ? (
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onRetry(message);
								}}
							>
								重试
							</button>
						) : undefined
					}
				>
					{message.body}
				</MessageBubble>
			</div>
		</div>
	);
}


interface MessageThreadDialogSurfaceProps {
	readonly dialog: MessageThreadDialog | null;
	readonly peerName: string;
	readonly isMuted: boolean;
	readonly isBlocked: boolean;
	readonly onClose: () => void;
	readonly onCopyMessage: (message: Message) => void;
	readonly onDeleteMessage: (message: Message) => void;
	readonly onForwardMessage: (message: Message) => void;
	readonly onConversationAction: (action: "mute" | "clear" | "block") => void;
}

function MessageThreadDialogSurface({
	dialog,
	peerName,
	isMuted,
	isBlocked,
	onClose,
	onCopyMessage,
	onDeleteMessage,
	onForwardMessage,
	onConversationAction,
}: Readonly<MessageThreadDialogSurfaceProps>) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const element = dialogRef.current;
		if (element === null) return;
		const open = dialog !== null;
		if (open) {
			if (!element.open && typeof element.showModal === "function") {
				element.showModal();
			}
		} else if (element.open) {
			element.close();
		}
	}, [dialog]);

	const title = useMemo(() => {
		if (dialog === null) return "";
		if (dialog.kind === "messageActions") return "消息操作";
		return `与 ${peerName} 的对话`;
	}, [dialog, peerName]);

	return (
		<dialog
			ref={dialogRef}
			className="message-thread-dialog"
			aria-labelledby="message-thread-dialog-title"
			onCancel={(event) => {
				event.preventDefault();
				onClose();
			}}
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
			onKeyDown={(event) => {
				if (event.key === "Escape") {
					event.preventDefault();
					onClose();
				}
			}}
		>
			{dialog === null ? null : (
				<div className="message-thread-dialog__body">
					<h2 id="message-thread-dialog-title">{title}</h2>
					{dialog.kind === "messageActions" ? (
						<div className="message-thread-dialog__actions message-thread-dialog__actions--stack">
							<button
								type="button"
								className="message-thread-dialog__option"
								onClick={() => onCopyMessage(dialog.message)}
							>
								<ArtistIcon name="doc" size={20} />
								<span>复制</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__option"
								onClick={() => onForwardMessage(dialog.message)}
							>
								<ArtistIcon name="share" size={20} />
								<span>转发</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__option message-thread-dialog__option--destructive"
								onClick={() => onDeleteMessage(dialog.message)}
							>
								<ArtistIcon name="trash" size={20} />
								<span>删除</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__secondary"
								onClick={onClose}
							>
								取消
							</button>
						</div>
					) : (
						<div className="message-thread-dialog__actions message-thread-dialog__actions--stack">
							<button
								type="button"
								className="message-thread-dialog__option"
								onClick={() => onConversationAction("mute")}
							>
								<ArtistIcon name="info" size={20} />
								<span>{isMuted ? "恢复通知" : "静音会话"}</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__option message-thread-dialog__option--destructive"
								onClick={() => onConversationAction("clear")}
							>
								<ArtistIcon name="trash" size={20} />
								<span>清空对话</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__option message-thread-dialog__option--destructive"
								onClick={() => onConversationAction("block")}
							>
								<ArtistIcon name="shield" size={20} />
								<span>{isBlocked ? "解除屏蔽" : "屏蔽会话"}</span>
							</button>
							<button
								type="button"
								className="message-thread-dialog__secondary"
								onClick={onClose}
							>
								取消
							</button>
						</div>
					)}
				</div>
			)}
		</dialog>
	);
}


function MessageThreadSkeleton() {
	return (
		<div className="message-thread-skeleton" aria-hidden="true">
			<div className="message-thread-skeleton__row message-thread-skeleton__row--other">
				<Skeleton shape="rectangle" width={180} height={14} />
				<Skeleton shape="rectangle" width="60%" height={44} />
			</div>
			<div className="message-thread-skeleton__row message-thread-skeleton__row--mine">
				<Skeleton shape="rectangle" width="48%" height={44} />
			</div>
			<div className="message-thread-skeleton__row message-thread-skeleton__row--other">
				<Skeleton shape="rectangle" width={180} height={14} />
				<Skeleton shape="rectangle" width="70%" height={44} />
			</div>
			<div className="message-thread-skeleton__input">
				<Skeleton shape="capsule" width="100%" height={56} />
			</div>
		</div>
	);
}


function formatParticipants(participants: readonly string[]): string {
	const others = participants.filter(
		(name) => name !== SELF_PARTICIPANT_MARKER,
	);
	if (others.length === 0) return UNKNOWN_PEER_LABEL;
	return others.join("、");
}

function formatTimestamp(epochMs: number): string {
	const date = new Date(epochMs);
	if (Number.isNaN(date.getTime())) return "";
	const formatter = new Intl.DateTimeFormat("zh-Hans", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
	return formatter.format(date);
}

function scopeKeySafe(
	scope: ReturnType<typeof useArtistSession>["scope"],
): string {
	return scope === null ? "anon" : scopeKey(scope);
}


const DRAFTS_KEY = "artist.social-drafts.v1";
type DraftMap = Record<string, unknown>;

function readDraft(key: string): string {
	if (typeof window === "undefined") return "";
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		return key in record && typeof record[key] === "string"
			? (record[key] as string)
			: "";
	} catch {
		return "";
	}
}

function writeDraft(key: string, value: string): void {
	if (typeof window === "undefined") return;
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		record[key] = value;
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
	}
}

function clearDraft(key: string): void {
	if (typeof window === "undefined") return;
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		delete record[key];
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
	}
}
